import React, { forwardRef, memo, useContext, useMemo } from 'react';
import { HoverProps } from 'react-aria';

import { cn } from '../../../lib/css/className/className';
import { BaseFilterChip, BaseFilterChipProps, useFilterChipAddonSizes } from '../BaseFilterChip';
import { FilterChipContext } from '../FilterChipContext/FilterChipContext';
import { Badge } from '../../Badge';

const cls = cn('FilterChip');

export type FilterChipProps = {
    counterValue?: number;
} & Omit<BaseFilterChipProps, 'size' | 'color' | 'role' | 'width'> &
    Pick<HoverProps, 'onHoverStart' | 'onHoverEnd'>;

/**
 * Компонент для отображения составной части фильтра – чипса.
 * Использует размер, цвет и тип из контекста. Предназначен для использования внутри FilterItem
 */
export const FilterChip = memo(
    forwardRef<HTMLDivElement, FilterChipProps>(function FilterChip(
        { addonRight, className, counterValue, ...props },
        forwardedRef,
    ) {
        const { size, color, role, width } = useContext(FilterChipContext);

        const { badgeSize: counterSize } = useFilterChipAddonSizes(size);

        const addonRightWithCounter = useMemo(() => {
            return (
                <>
                    {counterValue ? (
                        <Badge size={counterSize} color="blue" isDisabled={props.isDisabled}>
                            {counterValue}
                        </Badge>
                    ) : undefined}
                    {addonRight}
                </>
            );
        }, [counterValue, addonRight, counterSize, props.isDisabled]);

        return (
            <BaseFilterChip
                {...props}
                ref={forwardedRef}
                className={cls({}, [className])}
                size={size}
                color={color}
                role={role}
                width={width}
                addonRight={addonRightWithCounter}
            />
        );
    }),
);
